import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader'
import { ReactComponent as OffersIcon } from '../assets/images/backgrounds/passkey.svg'
import { PasskeyIcon } from '../components/icons'
import Modal from '../components/ui/modal'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import { useClient } from '../context/ClientContext'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import { PasswordInput } from '../components/ui/password-input'
import { getTenantBannerImageUrl } from '../utils/imageUtils'
import fallbackBanner from '../assets/images/backgrounds/phonepay.png'

const SignInForm = () => {
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const [searchParams] = useSearchParams()
  const { tenant } = useTenant()
  const { t, languageContent, languageId } = useLanguage()
  const { hydrateFromSignIn } = useClient()
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [renderError, setRenderError] = useState(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const mobileBannerSrc =
    getTenantBannerImageUrl(
      tenant?.tenantcontentconfig?.images,
      'signin',
      tenant?.tenanturlname,
      'mob',
    ) || fallbackBanner
  const desktopBannerSrc =
    getTenantBannerImageUrl(
      tenant?.tenantcontentconfig?.images,
      'signin',
      tenant?.tenanturlname,
      'web',
    ) || fallbackBanner
  const bannerAlt = t('common.bannerAlt')
  const safeBannerAlt = typeof bannerAlt === 'string' ? bannerAlt : 'Banner'

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState({})
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false)

  // Email verification token validation
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    type: '', // 'success' or 'error'
    message: '',
  })

  // Check for email verification token on component mount
  useEffect(() => {
    const token = searchParams.get('token')
    if (token && tenant?.tenantid) {
      validateEmailChangeToken(token)
    }
  }, [searchParams, tenant?.tenantid])

  const validateEmailChangeToken = async (token) => {
    try {
      const response = await apiService.post(
        '/api/clients/validateemailchangetoken',
        {
          token: token,
          tenantid: tenant?.tenantid,
        },
      )

      // Success response (OWMS0001)
      if (response.code === 'OWMS0001' && response.isValid) {
        setVerificationModal({
          isOpen: true,
          type: 'success',
          message:
            t('signin.emailVerification.success') ||
            'Your email has been successfully verified!',
        })
      }
    } catch (error) {
      console.error('Email verification token validation error:', error)

      // Handle different error codes
      let errorMessage =
        t('signin.emailVerification.error') || 'Email verification failed.'

      // apiService.handleError transforms to { message, status, data, isRateLimit }
      if (error.data?.code) {
        const errorCode = error.data.code
        switch (errorCode) {
          case 'OWER0002':
            errorMessage =
              t('signin.emailVerification.invalidToken') ||
              'Invalid or expired verification token.'
            break
          case 'OWER0003':
            errorMessage =
              t('signin.emailVerification.expiredToken') ||
              'Verification token has expired.'
            break
          case 'OWER0004':
            errorMessage =
              t('signin.emailVerification.noPendingChange') ||
              'No pending email change found.'
            break
          case 'OWER0005':
            errorMessage =
              t('signin.emailVerification.serverError') ||
              'Server error occurred. Please try again later.'
            break
          default:
            errorMessage = error.data.message || errorMessage
        }
      }

      setVerificationModal({
        isOpen: true,
        type: 'error',
        message: errorMessage,
      })
    }

    // Auto-close modal after 3 seconds
    setTimeout(() => {
      setVerificationModal({
        isOpen: false,
        type: '',
        message: '',
      })
    }, 3000)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    // Clear HTML5 validation message
    e.target.setCustomValidity('')
  }

  // Intercept HTML5 validation and show translated errors
  const handleInvalid = (e) => {
    e.preventDefault()
    const { name, validity } = e.target
    const newErrors = { ...errors }

    if (name === 'email') {
      if (validity.valueMissing) {
        newErrors.email = t('signin.errors.emailRequired')
      } else if (validity.typeMismatch) {
        newErrors.email = t('signin.errors.emailInvalid')
      }
    } else if (name === 'password') {
      if (validity.valueMissing) {
        newErrors.password = t('signin.errors.passwordRequired')
      }
    }

    setErrors(newErrors)
    // Set custom validity to prevent browser's default message
    e.target.setCustomValidity(' ')
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate email (allows + sign for existing users)
    if (!formData.email) {
      newErrors.email = t('signin.errors.emailRequired')
    } else if (!Validator.isValidEmailForLogin(formData.email)) {
      newErrors.email = t('signin.errors.emailInvalid')
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = t('signin.errors.passwordRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    // Track login CTA click for GTM
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'cta_click', ctaName: 'login' })
    }

    try {
      // Execute reCAPTCHA v3
      let recaptchaToken = null
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('login')
        } catch (error) {
          console.error('reCAPTCHA execution failed:', error)
        }
      }

      // Call API to login
      const response = await apiService.post('/api/kc/signin', {
        clientemail: formData.email,
        password: formData.password,
        clienttenantid: tenant?.tenantid || 1011, //undefined
        defaultlanguageid: languageId, // Send current language selection
        recaptchaToken, // Include reCAPTCHA token
      })

      // Login successful
      // Hydrate ClientContext — this sets isAuthenticated=true, which triggers
      // ProtectedRoute (isPublicOnly) to redirect. ProtectedRoute handles
      // the spin_email_redirect sessionStorage check and redirects accordingly.
      hydrateFromSignIn(response)
    } catch (error) {
      console.error('Login error:', error)

      // Clear password and captcha on error
      setFormData((prev) => ({ ...prev, password: '' }))
      setIsCaptchaChecked(false)

      // Handle API errors
      const errorCode = error?.data?.code
      const errorMessage = error?.data?.message
      const keycloakError = error?.data?.keycloakError
      const isEmailNotVerifiedError =
        errorMessage === 'Account is not fully set up' ||
        keycloakError === 'invalid_grant'
      const isAccountLocked =
        errorMessage?.includes('temporarily locked') ||
        errorMessage?.includes('multiple failed attempts')

      // Check for account lockout error
      if (isAccountLocked) {
        // Use translation for account locked message
        setErrors({ submit: t('signin.errors.accountLocked') })
      } else if (isEmailNotVerifiedError) {
        setErrors({ submit: t('signin.errors.emailNotVerified') })
      } else if (error.status === 401) {
        setErrors({ submit: t('signin.errors.invalidCredentials') })
      } else if (error.status === 403) {
        setErrors({ submit: t('signin.errors.emailNotVerified') })
      } else if (
        error.isRateLimit ||
        error.message === 'Rate limit exceeded. Please try again later.'
      ) {
        setErrors({ submit: t('signin.errors.rateLimitExceeded') })
      } else {
        setErrors({ submit: t('signin.errors.loginFailed') })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && <Loader fullscreen label={t('signin.loading')} size="xl" />}

      <div className="sign-in-page">
        {/* Banner - START */}
        <div className="main-banner max-w-md mx-auto relative">
          <div className="w-full flex justify-center">
            {/* Mobile banner */}
            <img
              src={mobileBannerSrc}
              alt={safeBannerAlt}
              className="block md:hidden max-[574px]:w-[300px] max-[574px]:max-w-[300px] min-[575px]:max-w-[400px] object-contain object-center"
              loading="lazy"
              onError={(e) => {
                e.target.src = fallbackBanner
              }}
            />
            {/* Desktop banner */}
            <img
              src={desktopBannerSrc}
              alt={safeBannerAlt}
              className="hidden md:block max-[574px]:w-[300px] max-[574px]:max-w-[300px] min-[575px]:max-w-[400px] object-contain object-center"
              loading="lazy"
              onError={(e) => {
                e.target.src = fallbackBanner
              }}
            />
          </div>

          {/* <div className="w-full px-6 absolute top-[30px] mt-6">
            <div aria-live="polite" className="mx-auto">
              <div className="bg-theme-accent-foreground text-theme-neutral rounded-[40px] h-[40px] flex justify-center items-center text-xs font-normal shadow-md">
                {t('signin.emailVerifiedBadge') || 'Email verified'}
              </div>
            </div>
          </div> */}
        </div>
        {/* Banner - END */}

        <div className="w-full px-6 md:max-w-md md:mx-auto">
          {/* Sign In - START */}
          <form
            className="form-container"
            onSubmit={handleSubmit}
            aria-label="Sign in form"
          >
            <div className="text-center mb-4">
              <h1 className="text-base font-extrabold text-theme-secondary">
                {t('signin.title')}
              </h1>
              <p className="text-xs text-theme-secondary mt-2">
                {t('signin.subtitle2.text1')}{' '}
                <a
                  href={`/${tenanturlname}/create-account`}
                  className="text-xs text-theme-primary hover:text-theme-primary/80 inline-block"
                >
                  {t('signin.subtitle2.text2')}
                </a>{' '}
              </p>
            </div>

            {/* Display general error */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid gap-1">
                <label
                  htmlFor="email"
                  className="text-sm font-normal text-theme-accent text-left"
                >
                  {t('signin.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onInvalid={handleInvalid}
                  required
                  className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                  placeholder={t('signin.emailPlaceholder')}
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p
                    id="email-error"
                    className="text-sm text-red-500 mt-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {errors.email}
                    </div>
                  </p>
                )}
              </div>

              <div className="grid gap-1">
                <label
                  htmlFor="password"
                  className="text-sm font-normal text-theme-accent text-left"
                >
                  {t('signin.passwordLabel')}
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onInvalid={handleInvalid}
                  required
                  className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                  placeholder={t('signin.passwordPlaceholder')}
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={
                    errors.password ? 'password-error' : undefined
                  }
                />
                {errors.password && (
                  <p
                    id="password-error"
                    className="text-sm text-red-500 mt-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {errors.password}
                    </div>
                  </p>
                )}
              </div>

              {/* <div className="mt-2">
                <div className="flex items-center justify-between py-6 px-3 rounded-md border border-gray-200 bg-gray-50">
                  <label className="inline-flex items-center gap-3 cursor-pointer w-full text-left">
                    <input
                      type="checkbox"
                      checked={isCaptchaChecked}
                      onChange={(e) => setIsCaptchaChecked(e.target.checked)}
                      aria-label="I am not a robot"
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {t('signin.captchaText')}
                    </span>
                  </label>
                  <div className="text-xs text-gray-400">
                    {t('signin.captchaLabel')}
                  </div>
                </div>
              </div> */}

              <Button
                id="sign-in-btn"
                variant="primary"
                size="lg"
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {t('signin.submitButton')}
              </Button>

              <div className="mt-0 hidden">
                <Button
                  id="passkey-signin-btn"
                  variant="secondary"
                  size="lg"
                  type="button"
                  className="w-full"
                  // onClick={() => setIsModalOpen(true)}
                  disabled="disabled"
                >
                  <PasskeyIcon className="!w-[23px] !h-[23px]" />
                  {t('signin.passkeyButton')}
                </Button>
              </div>

              <div className="mt-3">
                <div className="flex flex-col gap-2 text-center">
                  <a
                    href={`/${tenanturlname}/forgot-password`}
                    className="text-sm text-theme-primary hover:text-theme-primary/80 underline-offset-2"
                    aria-label="Set or reset your password"
                  >
                    {t('auth.setResetPasswordLink')}
                  </a>
                </div>
              </div>
              {/* Email verification modal */}
              <Modal
                open={verificationModal.isOpen}
                onClose={() =>
                  setVerificationModal({ isOpen: false, type: '', message: '' })
                }
                type={verificationModal.type}
                title={
                  verificationModal.type === 'success'
                    ? t('signin.emailVerification.successTitle') ||
                      'Email Verified'
                    : t('signin.emailVerification.errorTitle') ||
                      'Verification Failed'
                }
                message={verificationModal.message}
              />

              {/* Simple modal triggered by Sign Up (no validation) */}
              <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
            </div>
          </form>
          {/* Sign In - END */}
        </div>
      </div>
    </>
  )
}

const SignIn = () => {
  const { tenant } = useTenant()
  const [recaptchaSiteKey, setRecaptchaSiteKey] = React.useState(null)

  React.useEffect(() => {
    // Wait for tenant to load, then set the recaptcha key
    const key =
      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
      tenant?.tenantmasterconfig?.recaptcha?.siteKey
    setRecaptchaSiteKey(key)
  }, [tenant])

  // Don't render until we have the key
  if (!recaptchaSiteKey) {
    return null
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      language="en"
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
      <SignInForm />
    </GoogleReCaptchaProvider>
  )
}

export default SignIn
